import type { ApiSuccess } from '@mds/shared/types/api';
import type {
  AdminBookingFeedbackResponseList,
  BookingFeedbackForm,
  BookingFeedbackFormKind,
  BookingFeedbackSpace,
  ReplaceBookingFeedbackFormRequest,
  UpdateBookingFeedbackSpaceRequest,
} from '@mds/shared/types/booking-feedback';

import { api } from '@/lib/api';

/** The booking feedback settings and responses — see `backend/docs/booking-feedback.md`. */

/** A kind's default form, asked by every space of that kind without its own. */
export const getDefaultForm = (kind: BookingFeedbackFormKind): Promise<BookingFeedbackForm> =>
  api
    .get<ApiSuccess<BookingFeedbackForm>>(`admin/booking-feedback/forms/${kind}`)
    .then((r) => r.data);

/**
 * Replaces the whole form in one go. `expectedVersion` is the version this copy
 * was loaded at; a stale one is refused rather than overwriting someone else.
 */
export const replaceDefaultForm = (
  kind: BookingFeedbackFormKind,
  body: ReplaceBookingFeedbackFormRequest,
): Promise<BookingFeedbackForm> =>
  api
    .put<ApiSuccess<BookingFeedbackForm>>(`admin/booking-feedback/forms/${kind}`, body)
    .then((r) => r.data);

/** Every space, with its switch and whether it asks its own form. */
export const listSpaces = (): Promise<BookingFeedbackSpace[]> =>
  api.get<ApiSuccess<BookingFeedbackSpace[]>>('admin/booking-feedback/spaces').then((r) => r.data);

export const updateSpace = (
  resourceId: string,
  body: UpdateBookingFeedbackSpaceRequest,
): Promise<BookingFeedbackSpace> =>
  api
    .patch<ApiSuccess<BookingFeedbackSpace>>(`admin/booking-feedback/spaces/${resourceId}`, body)
    .then((r) => r.data);

/** A space's own form: `404 BOOKING_FEEDBACK_FORM_NOT_FOUND` while it asks its default. */
export const getSpaceForm = (resourceId: string): Promise<BookingFeedbackForm> =>
  api
    .get<ApiSuccess<BookingFeedbackForm>>(`admin/booking-feedback/spaces/${resourceId}/form`)
    .then((r) => r.data);

/** Creates a space's own form (`expectedVersion` 0) or replaces it. */
export const replaceSpaceForm = (
  resourceId: string,
  body: ReplaceBookingFeedbackFormRequest,
): Promise<BookingFeedbackForm> =>
  api
    .put<ApiSuccess<BookingFeedbackForm>>(`admin/booking-feedback/spaces/${resourceId}/form`, body)
    .then((r) => r.data);

/** Removes a space's own form, so it asks its kind's default again. */
export const removeSpaceForm = (resourceId: string): Promise<null> =>
  api.delete<null>(`admin/booking-feedback/spaces/${resourceId}/form`);

/** A type alias, so it carries the index signature the client's `query` needs. */
export type ResponseQuery = {
  page?: number;
  limit?: number;
  direction?: 'asc' | 'desc';
  kind?: BookingFeedbackFormKind;
  resourceId?: string;
  rating?: number;
};

/** Not anonymous: each response names the client and the booking it is about. */
export const listResponses = (query: ResponseQuery = {}) =>
  api.get<AdminBookingFeedbackResponseList>('admin/booking-feedback/responses', { query });
